import {
  AUTH_BASE_PATH,
  LOGIN_PATH,
  LOGOUT_PATH,
  REFRESH_PATH,
  REGISTER_PATH,
  EXCHANGE_PATH,
  User,
  MESSAGE_TOPICS,
  SOURCE_QUERY_PARAM,
  ERROR_CALLED_REFRESH_FOR_NOT_LOGGEDIN_USER,
  AUTH_STATE_EXPIRY_TRESHOLD,
  AuthError,
  AUTH_EVENTS,
} from '@move-auth/shared';

import { buildLoginUrl, buildRegisterUrl } from './buildLoginAndRegisterUrl';
import buildLogoutUrl from './buildLogoutUrl';
import iframeAuthentication from './iframeAuthentication';
import onPageVisibilityChange from './onPageVisibilityChange';
import fetchToken from './fetchToken';
import getAuthState from './getAuthState';
import {
  noop,
  setSignedInViaHorizontal,
  wasSignedInViaHorizontal,
} from './utils';

export default class WebClient {
  constructor({
    locale,
    basePath,
    rootPath,
    logger,
    onAuth,
    onAuthError,
    onAutoLogin,
    onAutoLoginError,
    user,
    checkInterval,
    forceAuthentication,
    horizontalUserSigninEnabled,
    horizontalTrustedTokenUrl,
  } = {}) {
    this.locale = locale || 'en';
    this.basePath = basePath || AUTH_BASE_PATH;
    this.rootPath = rootPath || '';
    this.onAuth = onAuth || noop;
    this.onAuthError = onAuthError || noop;
    this.onAutoLogin = onAutoLogin || noop;
    this.onAutoLoginError = onAutoLoginError || noop;
    this.logger = logger || { log: () => {}, error: () => {} };
    this.horizontalUserSigninEnabled = Boolean(horizontalUserSigninEnabled);
    this.horizontalTrustedTokenUrl = horizontalTrustedTokenUrl;

    this.user = new User(user);
    this.logger.log(
      '@move-auth/web-client - user on initializing',
      this.user.toJSON()
    );

    this.checkInterval = checkInterval || 10; // seconds

    // When enabled, the token will be refrehed even when it is still valid.
    // Warning: enabling this will increase the load for the identity service dramatically -
    // for manual testing / e2e tests use only!
    this.forceAuthentication = forceAuthentication || false;
  }

  async start() {
    this.handlePageVisibilityChange();

    /*
      Run the horizontal auto-login only when
        - the horizontal auto-login feature is enabled
        - the user was not already signed in with another method
        - the user is already migrated to CIS
        - the user was not already signed in via auto-login and
          might have logged out intentionally
    */
    if (
      !this.isLoggedIn() &&
      this.horizontalUserSigninEnabled &&
      !wasSignedInViaHorizontal()
    ) {
      this.signInHorizontalUser();
    }

    return this.getUserData();
  }

  login(params = {}, sourceUrl = null) {
    const loginUrl = buildLoginUrl({
      path: this.getPath(LOGIN_PATH),
      locale: this.locale,
      params,
      sourceUrl,
    });

    window.location.assign(loginUrl);
  }

  register(params = {}, sourceUrl = null) {
    const registerUrl = buildRegisterUrl({
      path: this.getPath(REGISTER_PATH),
      locale: this.locale,
      params,
      sourceUrl,
    });
    window.location.assign(registerUrl);
  }

  logout(fragment = '', sourceUrl = null) {
    const logoutUrl = buildLogoutUrl({
      path: this.getPath(LOGOUT_PATH),
      fragment,
      sourceUrl,
    });
    window.location.assign(logoutUrl);
  }

  leave(sourceUrl) {
    const logoutUrl = buildLogoutUrl({
      path: this.getPath(LOGOUT_PATH),
      sourceUrl,
      authEvent: AUTH_EVENTS.USER_LEAVE,
    });
    window.location.assign(logoutUrl);
  }

  async debouncedRefresh(
    { forceAuthentication } = {
      forceAuthentication: this.forceAuthentication,
    }
  ) {
    if (this.refreshingPromise === undefined) {
      this.refreshingPromise = new Promise((resolve, reject) => {
        this.refresh({ forceAuthentication })
          .then(result => {
            this.refreshingPromise = undefined;
            resolve(result);
          })
          .catch(error => {
            this.refreshingPromise = undefined;
            reject(error);
          });
      });
    }
    return this.refreshingPromise;
  }

  async refresh(
    { forceAuthentication } = {
      forceAuthentication: this.forceAuthentication,
    }
  ) {
    if (!this.hasAuthStateCookie()) {
      const error = new Error(ERROR_CALLED_REFRESH_FOR_NOT_LOGGEDIN_USER);
      this.user = new User();
      throw error;
    }

    if (!forceAuthentication && this.isTokenActive()) {
      this.logger.log(
        '@move-auth/web-client',
        'user still active until',
        new Date(Number(getAuthState().exp) * 1000)
      );
      return this.user;
    }

    const loginUrl = buildLoginUrl({
      path: this.getPath(REFRESH_PATH),
    });

    try {
      const user = await fetchToken(loginUrl);
      this.user = new User(user);
      this.logger.log(
        '@move-auth/web-client - user on refresh',
        this.user.toJSON()
      );
      this.onAuth(this.getUserData());
      return this.user;
    } catch (error) {
      this.user = new User();
      this.onAuthError(error, this.getUserData());
      this.logger.error('@move-auth/web-client - refresh', error);
      throw error;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  isTokenActive() {
    const { exp } = getAuthState();
    return exp && Date.now() < (exp - AUTH_STATE_EXPIRY_TRESHOLD) * 1000;
  }

  // eslint-disable-next-line class-methods-use-this
  hasAuthStateCookie() {
    const { exp } = getAuthState();
    return Boolean(exp);
  }

  isLoggedIn() {
    return this.user.isLoggedIn();
  }

  getUserData() {
    const {
      email,
      userId,
      username,
      userIdEncrypted,
      emailEncrypted,
      picture,
    } = this.user;
    return {
      isLoggedIn: this.user.isLoggedIn(),
      email,
      userId,
      username,
      userIdEncrypted,
      emailEncrypted,
      picture,
    };
  }

  handlePageVisibilityChange() {
    onPageVisibilityChange({
      onVisible: () => {
        if (this.hasAuthStateCookie() && !this.isLoggedIn()) {
          // if not logged in (user is probably logged in in another tab) refresh
          this.refresh({ forceAuthentication: true }).catch(() => {
            /* ignore error */
          });
        }

        if (!this.hasAuthStateCookie() && this.isLoggedIn()) {
          // if there is no auth state cookie, logged in state is wrong (user probably logged out in another tab)
          this.user = new User();
          this.onAuthError();
        }
      },
    });
  }

  async signInHorizontalUser() {
    try {
      if (!this.horizontalTrustedTokenUrl) {
        throw new AuthError('horizontalTrustedTokenUrl not set');
      }

      const currentUrl = window.location.href;

      const tokenExchangeUrl = new URL(this.getPath(EXCHANGE_PATH), currentUrl);
      tokenExchangeUrl.searchParams.set(
        SOURCE_QUERY_PARAM,
        new URL('/', currentUrl)
      );
      tokenExchangeUrl.searchParams.set('topic', MESSAGE_TOPICS.AUTO_LOGIN);

      const horizontalTrustedTokenUrl = new URL(this.horizontalTrustedTokenUrl);
      horizontalTrustedTokenUrl.searchParams.set(
        'redirect_uri',
        tokenExchangeUrl
      );

      const userData = await iframeAuthentication({
        topic: MESSAGE_TOPICS.AUTO_LOGIN,
        url: horizontalTrustedTokenUrl,
        timeout: 60 * 1000,
      });

      this.user = new User(userData);

      setSignedInViaHorizontal();

      this.logger.log('@move-auth/web-client - one-way-sso', this.user);

      this.onAutoLogin(this.getUserData());
    } catch (error) {
      this.onAutoLoginError(error);
      // eslint-disable-next-line
      this.logger.error('@move-auth/web-client - one-way-sso', error);
    }
  }

  getPath(endpoint = '') {
    return this.basePath + endpoint;
  }
}
